import { ComponentProps, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { InlineButton, Tag2 } from '@platform/ui'
import { Collection } from '@/store/api/showcasesApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { createFiltersSelector, setFilters } from '@/store/slices/aggregatesSlice'
import styles from './CollectionTagListComponent.module.scss'
import { FilterDrawerType } from '../CourseFiltersDrawer/types'

type Props = {
  type: FilterDrawerType
  collections: Collection[]
  onClick: (item: Collection) => void
}

const iconAfterProps: ComponentProps<typeof Tag2>['iconAfterProps'] = {
  name: 'CloseBoldStroke',
  title: 'Clear',
}

export const CollectionTagListComponent = ({ type, collections, onClick }: Props) => {
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const filters = useAppSelector((state) => createFiltersSelector(state, type))

  const handleClearClick = useCallback(() => {
    dispatch(setFilters({ type, filters: { ...filters, collection: [] } }))

    const searchParams = new URLSearchParams(location.search)
    searchParams.delete('collection')
    navigate({ search: searchParams.toString() }, { replace: true })
  }, [dispatch, filters, type, location.search, navigate])

  const shouldShowClear = collections.length > 0 && filters.collection.length > 0

  return (
    <div className={styles.wrapper} id="collection-tag-list">
      {collections.map((item) => {
        const isActive = filters.collection.includes(item.id)

        return (
          <Tag2
            key={item.id}
            text={i18n.exists(item.name) ? t(item.name) : item.name}
            onClick={() => onClick(item)}
            isActive={isActive}
            iconAfterProps={isActive ? iconAfterProps : undefined}
          />
        )
      })}
      {shouldShowClear ? (
        <InlineButton onClick={handleClearClick} size="small" text={t('collections.filter.clear')} />
      ) : null}
    </div>
  )
}
